import { Injectable } from '@angular/core';
import { Feature, Geometry } from 'geojson';
import { MathAux } from 'src/app/instant-analysis/services/math-aux/math-aux';
import { TimespanService } from 'src/app/instant-analysis/services/timespan/timespan.service';
import { RainIntensity, RainIntensityAuxiliar } from '../../models/rain-intensity';


@Injectable({
  providedIn: 'root'
})
export class DiscreteChartService {

  features: Feature<Geometry>[] = [];
  selectedOptions: string[] = [];

  constructor(private timespanService: TimespanService) { }

  setRainFeatures(features: Feature<Geometry>[]) {
    this.features = features;
  }

  /**
   * Calcula a porcentagem de tempo em cada intensidade de chuva para cada estação, usando 
   * os tempos globais da cidade em cada intensidade da cidade como base.
   * @returns array de registros de porcentagens de tempo separados por intensidade de chuva
   *    e estação. Cada estação tem 5 elementos na ordem fraca, moderada, forte, muito forte, e geral.
   */
  calcPercentageRainTimePerIntensity() {
    let rainTimesPerIntensity = this.calcAbsoluteRainTimePerIntensity();
    rainTimesPerIntensity = this.calcRainTimePercentages(rainTimesPerIntensity);
    return rainTimesPerIntensity.slice(5); // evita os registros de tempo globais da cidade (0 a 4)
  }

  /**
   * Conta a existencia de valores discretos  for todos os instantes de tempo
   * caso tenha 6 chuvas fracas, 2 moderadas e 1 forte, vai contar somente +1 para o total de fracas, moderadas e fortes
   * @returns retorna o o json de formação do gráfico
   */
  calcAbsoluteRainTimePerIntensity() {
    const cityRainTimes = this.calcRainTimePerIntensities(this.features, 'CIDADE DE NITERÓI');
    const filtered = this.features.filter(f => this.selectedOptions.includes(f.properties.nome_estacao_original));
    return cityRainTimes.concat(this.calcRainTimePerIntensities(filtered));
  }

  /**
   * Calcula os tempos de chuva para cada intensidade de chuva e estação.
   * @param features Registros das estações que terão os tempos de chuva calculados.
   * @param globalKey Chave usada para calcular os tempos das intensidades globalmente.
   *    Por exemplo, pode ser usado para calcular os tempos de chuva para a cidade como um todo.
   * @returns tempos de intensidade de chuva para cada estação, ou global.
   */
  calcRainTimePerIntensities(features: Feature<Geometry>[], globalKey: string = null) {

    let stations: {[key: string]: { auxiliar: RainIntensityAuxiliar, values: RainIntensity[] }} = {};
    features.sort((a, b) => { return a.properties.id_tempo - b.properties.id_tempo; });

    for (let intensityIdx = 0; intensityIdx < 5; intensityIdx++) {
      features.forEach((val, index) => {
        let { id_tempo, nome_estacao_original } = val.properties;
        nome_estacao_original = globalKey ? globalKey : nome_estacao_original;

        if (! (nome_estacao_original in stations) ) {
          stations[nome_estacao_original] = this.createStationRecord(nome_estacao_original);
        }

        let station = stations[nome_estacao_original];
        let values = station.values;
        let { lastTimeId, lastTimeIdPerIntensity, lastTimeRecords, foundRecordAtCurrentTime, intensityMillis } = station.auxiliar;

        if (id_tempo != lastTimeId) {
          if (foundRecordAtCurrentTime[intensityIdx] == false && lastTimeRecords[intensityIdx]) {
            values[intensityIdx].duracao += this.timespanService.calcDateDiff(
              lastTimeRecords[intensityIdx].properties,
              features[index - 1].properties
            ) / 1000 / 60 / 60;
            lastTimeRecords[intensityIdx] = null;
          }
          foundRecordAtCurrentTime[intensityIdx] = false;
        }
        station.auxiliar.lastTimeId = id_tempo;

        if (lastTimeIdPerIntensity[intensityIdx] != val.properties.id_tempo 
          && this.rainOnIntensity(val.properties.indice_pluv, intensityIdx)) {

          lastTimeIdPerIntensity[intensityIdx] = val.properties.id_tempo;

          if (lastTimeRecords[intensityIdx]) {
            values[intensityIdx].duracao += this.timespanService.calcDateDiff(
              lastTimeRecords[intensityIdx].properties, val.properties
            ) / 1000 / 60 / 60;
          }
          lastTimeRecords[intensityIdx] = val;
          foundRecordAtCurrentTime[intensityIdx] = true;
        }
      });
    }
    return [].concat(...Object.values(stations).map(s => s.values));
  }

  /**
   * Cria um objeto para registrar o tempo de cada intensidade de chuva para uma estação.
   * O objeto também tem variáveis auxiliares ao cálculo das durações.
   * @param stationName nome da estação cujos tempos serão registrados
   * @returns objeto inicializado para registrar os tempos de chuva da estação.
   */
  private createStationRecord(stationName: string): any {
    return {
      auxiliar: {
        lastTimeId: -1,
        lastTimeIdPerIntensity: [-1, -1, -1, -1, -1],
        lastTimeRecords: [null, null, null, null, null],
        foundRecordAtCurrentTime: [false, false, false, false, false],
        intensityMillis: [0, 0, 0, 0, 0]
      },
      values: [
        {'classificacao': 'fraca', 'color': '#339944', 'duracao': 0, 'porcentagem_tempo': 0, 'estacao': stationName},
        {'classificacao': 'moderada', 'color': '#FFCC00', 'duracao': 0, 'porcentagem_tempo': 0, 'estacao': stationName},
        {'classificacao': 'forte', 'color': '#FF8822', 'duracao': 0, 'porcentagem_tempo': 0, 'estacao': stationName},
        {'classificacao': 'muito forte', 'color': '#DD2233', 'duracao': 0, 'porcentagem_tempo': 0, 'estacao': stationName},
        {'classificacao': 'geral', 'color': '#555', 'duracao': 0, 'porcentagem_tempo': 0, 'estacao': stationName},
      ]
    };
  }

  /**
   * Calcula a porcentagem de tempo das intensidades de chuva para cada estação.
   * Espera que os 5 primeiros elementos sejam os tempos das intensidades da cidade globalmente.
   * Usa esses valores para calcular as porcentagens para as outras estações.
   * @param values registros de tempo das intensidades de chuva da cidade inteira e de cada estação.
   * @returns registros de tempo das intensidades de chuva, incluindo a porcentagem de tempo.
   */
  private calcRainTimePercentages(values: RainIntensity[]) {
    
    const cityIntensities = values.filter(v => v.estacao == 'CIDADE DE NITERÓI').map(v => v.duracao);
    for (let i = 5; i <= values.length - 5; i += 5) {
      for (let j = 0; j < cityIntensities.length; j++) {
        let station = values[i + j];
        station.porcentagem_tempo = cityIntensities[j] > 0 ? 
          MathAux.round(station.duracao / cityIntensities[j], 2) : 0;
      }
    }
    return values;
  }

  /**
   * Verifica se o valor do índice pluviométrico instantâneo está na intensidade especificada.
   * @param intensityIdx índice do tipo da intensidade de chuva.
   * @param rainValue valor do índice pluviométrico instantâneo.
   */
  private rainOnIntensity(rainValue: number, intensityIdx: number) {

    const rainPredicates = [
      (rain: number) => { return rain > 0 && rain < 0.625 }
      , (rain: number) => { return rain >= 0.625 && rain < 2.5 }
      , (rain: number) => { return rain >= 2.5 && rain < 12.5 }
      , (rain: number) => { return rain >= 12.5 }
      , (rain: number) => { return rain > 0 }
    ];

    const predicate = rainPredicates[intensityIdx];

    return predicate(rainValue);
  }
  
  /**
   * Imprime alguns atributos dos dados do gráfico em formato de tabela.
   * Usado apenas para auxiliar no debug da quantidade de tempo de chuva separada por intensidade.
   */
  _printChartDataAsTable() {

    let chartData = this.features.map(f => {
      let { ano, mes, dia, hora, minuto, nome_estacao_original, indice_pluv, } = f.properties;
      let intensity = this.getRainIntensity(indice_pluv);

      return { 
        tempo: `${ano}-${mes}-${dia} ${hora}:${minuto}`, 
        nome_estacao_original, 
        indice_pluv, 
        intensity
      }
    });

    console.table(chartData);
  }
  
  /**
   * Mapea um valor de índice pluviométrico instantâneo (indice_pluv) em uma string indicando a intensidade.
   * @param indicePluv valor do atributo indice_pluv de um registro de chuva
   * @returns 'fraca', 'moderada', 'forte' ou 'muito forte'
   */
  getRainIntensity(indicePluv: number) {
    
    if (indicePluv > 0 && indicePluv < 0.625) return 'fraca';
    if (indicePluv >= 0.625 && indicePluv < 2.5 ) return 'moderada';
    if (indicePluv >= 2.5 && indicePluv < 12.5) return 'forte';
    if (indicePluv >= 12.5) return 'muito forte';
    return 'sem chuva';
  }
}
